import { Row, Col, Button, Form, FormGroup, Input, Label, Table } from 'reactstrap';
import { useContext, useState, useEffect, useCallback } from 'react';
import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { Link } from 'react-router-dom';
import { Context as AuthContext } from '../../../contexts/AuthContext';
import EmployeeService from '../../../services/Employee';
import Messaging from '../../../services/Messaging';
import filterEligibleSupervisors from '../../../helpers/filterEligibleSupervisors';
import { toast } from 'react-toastify';
import { createOptionsPage } from '../../../entities/OptionsPaged';
import { createPagedMeta } from '../../../entities/PagedMeta';

type BulkUpdateProps = {
  isOpen: boolean;
};

enum BulkUpdateType {
  None = 'none',
  SupervisorChange = 'supervisorChange',
}

const SUPERVISOR_CHANGE_STATUSES = ['NEW', 'PENDING', 'OFFER_SENT', 'HIRED', 'ACTIVE', 'LOA'];

const BulkUpdate = ({ isOpen }: BulkUpdateProps) => {
  const { token, currentSchool }: any = useContext(AuthContext);
  const [activeType, setActiveType] = useState<BulkUpdateType>(BulkUpdateType.None);
  const [step, setStep] = useState(0);
  const [employees, setEmployees] = useState<any[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [searchStaffName, setSearchStaffName] = useState('');
  const [supervisors, setSupervisors] = useState<any[]>([]);
  const [selectedSupervisorId, setSelectedSupervisorId] = useState('');
  const [loading, setLoading] = useState(false);

  const loadEmployees = useCallback(async () => {
    try {
      const filteredOptions = {
        filters: [
          {
            field: 'status',
            operation: 'in',
            value: SUPERVISOR_CHANGE_STATUSES,
          },
        ],
        options: createOptionsPage(createPagedMeta({ perPage: 1000 })),
      };

      const result = await EmployeeService(token).listEmployeesBySchool(currentSchool.value, '', filteredOptions);
      const filteredEmployees = (result?.data || []).filter(
        (employee: any) => employee?.id && employee?.status && SUPERVISOR_CHANGE_STATUSES.includes(employee.status),
      );
      setEmployees(filteredEmployees);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to load employees.');
    }
  }, [token, currentSchool.value]);

  const loadSupervisors = useCallback(async () => {
    try {
      const result = await Messaging(token).listSelectStaff(currentSchool.value);
      setSupervisors(filterEligibleSupervisors(result.data));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to load supervisors.');
    }
  }, [token, currentSchool.value]);

  useEffect(() => {
    if (isOpen && activeType === BulkUpdateType.SupervisorChange && step === 0) {
      loadEmployees();
    }
  }, [isOpen, activeType, step, loadEmployees]);

  useEffect(() => {
    if (isOpen && activeType === BulkUpdateType.SupervisorChange && step === 1) {
      loadSupervisors();
    }
  }, [isOpen, activeType, step, loadSupervisors]);

  const handleTypeSelect = (type: BulkUpdateType) => {
    setActiveType(type);
    setStep(0);
    setSelectedEmployees([]);
    setSelectAll(false);
    setSearchStaffName('');
    setSelectedSupervisorId('');
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    } else {
      setActiveType(BulkUpdateType.None);
      setSelectedEmployees([]);
      setSelectAll(false);
      setSearchStaffName('');
      setSelectedSupervisorId('');
    }
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(employees.map((emp) => emp.id));
    }
    setSelectAll(!selectAll);
  };

  const toggleEmployeeSelection = (employeeId: string) => {
    const newSelection = selectedEmployees.includes(employeeId)
      ? selectedEmployees.filter((id) => id !== employeeId)
      : [...selectedEmployees, employeeId];
    setSelectedEmployees(newSelection);
    setSelectAll(newSelection.length === employees.length);
  };

  const handleNext = () => {
    if (selectedEmployees.length === 0) {
      toast.error('Please select at least one employee.');
      return;
    }
    setStep(1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    if (!selectedSupervisorId) {
      toast.error('Please select a supervisor.');
      return;
    }

    setLoading(true);

    try {
      await EmployeeService(token).bulkUpdateSupervisors({
        employeeIds: selectedEmployees,
        supervisorId: selectedSupervisorId,
      });

      toast.success(`Supervisor updated for ${selectedEmployees.length} employee(s)!`);
      setSelectedEmployees([]);
      setSelectAll(false);
      setSelectedSupervisorId('');
      setStep(0);
    } catch (e: any) {
      const message = String(e?.response?.data?.message || 'An error occurred!');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (activeType === BulkUpdateType.None) {
    return (
      <Row>
        <Col>
          <UncontrolledDropdown>
            <DropdownToggle caret color="primary">
              <i className="fas fa-plus mr-2" />
              Create New
            </DropdownToggle>
            <DropdownMenu>
              <DropdownItem onClick={() => handleTypeSelect(BulkUpdateType.SupervisorChange)}>
                Supervisor Change
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </Col>
      </Row>
    );
  }

  if (activeType === BulkUpdateType.SupervisorChange) {
    if (step === 0) {
      return (
        <div className="pt-4 pl-5 pr-5 pb-4">
          <Row className="mb-3">
            <Col>
              <h2 className="mb-0">Who's Supervisor Are You Changing?</h2>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col lg="4">
              <Input
                placeholder="Search by Name"
                value={searchStaffName}
                onChange={(e) => setSearchStaffName(e.target.value)}
              />
            </Col>
          </Row>
          <Table className="align-items-center table-flush" responsive>
            <thead className="thead-light">
              <tr>
                <th>
                  <Input type="checkbox" checked={selectAll} onChange={toggleSelectAll} />
                </th>
                <th>Name</th>
                <th>Current Supervisor</th>
              </tr>
            </thead>
            <tbody>
              {employees?.map((employee) => {
                const fullName = `${employee?.user?.firstName || ''} ${employee.user?.lastName || ''}`.trim();
                const isVisible =
                  searchStaffName === '' || fullName.toLowerCase().includes(searchStaffName.toLowerCase());

                const supervisorName = employee?.supervisor?.name || '-';

                return (
                  <tr key={employee?.id} style={{ display: isVisible ? '' : 'none' }}>
                    <td>
                      <Input
                        type="checkbox"
                        checked={selectedEmployees.includes(employee?.id)}
                        onChange={() => toggleEmployeeSelection(employee?.id)}
                      />
                    </td>
                    <td>
                      <Link to={`/users/${employee?.user?.id}/profile`} target="_blank" rel="noopener noreferrer">
                        {fullName || '-'}
                      </Link>
                    </td>
                    <td>{supervisorName}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          <Row className="mt-5">
            <Col className="text-right">
              <Button color="secondary" onClick={handleBack} className="mr-2">
                Back
              </Button>
              <Button color="primary" onClick={handleNext} disabled={selectedEmployees.length === 0}>
                Next
              </Button>
            </Col>
          </Row>
        </div>
      );
    }

    if (step === 1) {
      return (
        <div className="pt-4 pl-5 pr-5 pb-4">
          <Form onSubmit={handleSubmit}>
            <Row className="mb-4">
              <Col>
                <h2 className="mb-0">Select New Supervisor</h2>
              </Col>
            </Row>
            <Row>
              <Col lg="6">
                <FormGroup>
                  <Label className="form-control-label">New Supervisor</Label>
                  <Input
                    type="select"
                    value={selectedSupervisorId}
                    onChange={(e) => setSelectedSupervisorId(e.target.value)}
                    required
                  >
                    <option value="">Select a supervisor</option>
                    {supervisors.map((supervisor: any) => {
                      const supervisorName = `${supervisor.firstName || ''} ${supervisor.lastName || ''}`.trim();
                      const displayName = supervisor?.roles?.includes('admin')
                        ? `${supervisorName} - Admin`
                        : `${supervisorName} - ${supervisor?.group?.name || ''}`;
                      return (
                        <option key={supervisor.userId} value={supervisor.userId}>
                          {displayName}
                        </option>
                      );
                    })}
                  </Input>
                </FormGroup>
              </Col>
            </Row>
            <Row className="mt-5">
              <Col className="text-right">
                <Button color="secondary" onClick={handleBack} className="mr-2">
                  Back
                </Button>
                <Button color="primary" type="submit" disabled={loading || !selectedSupervisorId}>
                  {loading ? 'Saving...' : 'Save'}
                </Button>
              </Col>
            </Row>
          </Form>
        </div>
      );
    }
  }

  return null;
};

export default BulkUpdate;
